<script setup lang="ts">
import CUpload from './Upload.vue';
import { useFilemanagerUploadStore, type FilemanagerUploadResult } from '../../runtime/stores/filemanagerUpload';

const store = useFilemanagerUploadStore();
const { $events } = useEvents();
const { $alert } = useAlerts();
const { $t } = useLocalization();
const router = useRouter();
const uploader = ref<InstanceType<typeof CUpload> | null>(null);
const uploadTarget = ref<HTMLElement | null>(null);
const actionsTarget = `global-file-upload-actions-${useId()}`;

const uploadDisabled = computed(() => store.busy || !!uploader.value?.uploading || !store.preparationOpen);

function onDropFiles(payload: { files: File[] }, event: DragEvent)
{
    event.stopPropagation();

    if (uploadDisabled.value)
        return;

    const items = Array.from(event.dataTransfer?.items ?? []);
    let files = payload.files;
    let hasDirectories = false;

    if (items.length)
    {
        files = items.flatMap(item =>
        {
            if (item.kind !== 'file')
                return [];

            if (item.webkitGetAsEntry?.()?.isDirectory)
            {
                hasDirectories = true;
                return [];
            }

            const file = item.getAsFile();

            return file ? [file] : [];
        });
    }

    if (hasDirectories)
        $alert.warning($t('[[[Przeciągnij pliki, nie foldery.]]]'));

    if (files.length)
        store.setFiles([...store.files, ...files], store.destination, store.owner);
}

function onDragOverFiles(_data: unknown, event: DragEvent)
{
    if (!uploadDisabled.value && event.dataTransfer?.types.includes('Files'))
        event.dataTransfer.dropEffect = 'copy';
}

watch(() => uploader.value?.uploading, value => store.busy = !!value);
watch(() => uploader.value?.progressPercent, value => store.percent = value ?? 0);
watch(() => store.progressRequest, () => uploader.value?.showProgress());

function showResults(items: FileResource[])
{
    if (!items.length || !store.completed) return;
    const request: FilemanagerUploadResult & { handled: boolean } = { ...store.completed, items, handled: false };
    $events.$emit('filemanager::upload::show-results', request);
    if (!request.handled)
    {
        request.owner = '';
        $events.$emit('filemanager::upload::show-results', request);
    }
    if (!request.handled)
        void router.push({ name: 'filemanager', query: { resource: items[0].publicId } });
}
</script>

<template>
    <div class="hidden">
        <Teleport :disabled="!uploadTarget" :to="uploadTarget || 'body'">
            <CUpload ref="uploader" v-model="store.files" :scope="store.destination.scope" :folder="store.destination.folder" :actions-target="actionsTarget" @upload-start="store.start" @send="store.finish" @review="store.preparationOpen = true" @show-results="showResults" @cancel="store.preparationOpen = false" />
        </Teleport>
    </div>
    <IdeoModal v-model:open="store.preparationOpen" :title="$t('[[[Wysyłanie plików]]]')" :width="620" keep-rendered :hide-footer="!store.files.length && !store.busy" footer-layout="custom" layer="nested">
        <p class="m-0 mb-[16px] text-ui-helper text-fg-muted dark:text-fg-muted-dark">{{ $t('[[[Folder docelowy]]]') }}: <span class="font-[500] text-fg dark:text-fg-dark">{{ store.destination.name || $t('[[[Folder główny]]]') }}</span></p>
        <IdeoDropZone accept-files unstyled class="rounded-[8px] border border-dashed border-border-strong [transition:border-color_0.14s_ease] motion-reduce:transition-none data-[accepted=true]:border-accent data-[accepted=false]:border-danger data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-[0.55] dark:border-border-strong-dark dark:data-[accepted=true]:border-accent dark:data-[accepted=false]:border-danger" :accept="() => false" :disabled="uploadDisabled" :label="$t('[[[Dodawanie plików do kolejki]]]')" @drop="onDropFiles" @drag-over="onDragOverFiles">
            <template #default="{ over, accepted }">
                <div v-if="!store.files.length && !store.busy" class="flex flex-col items-center gap-[10px] px-[16px] py-[32px] text-center">
                    <IdeoIcon name="cloud-upload" :size="32" class="text-fg-faint dark:text-fg-faint-dark" aria-hidden="true" />
                    <span class="text-ui-body" role="status">{{ over && accepted ? $t('[[[Upuść pliki, aby dodać je do kolejki]]]') : $t('[[[Przeciągnij tutaj pliki lub wybierz je z dysku]]]') }}</span>
                    <IdeoAction variant="primary" icon="folder-open" :disabled="uploadDisabled" :text="$t('[[[Wybierz z dysku]]]')" @click="uploader?.openDialog()" />
                </div>
                <div v-else class="flex items-center justify-center gap-[8px] px-[12px] pt-[12px] text-center text-ui-helper text-fg-muted dark:text-fg-muted-dark" role="status">
                    <IdeoIcon name="cloud-upload" :size="20" class="shrink-0" aria-hidden="true" />
                    <span class="min-w-0 whitespace-normal">{{ over && accepted ? $t('[[[Upuść pliki, aby dodać je do kolejki]]]') : $t('[[[Możesz przeciągnąć tutaj kolejne pliki]]]') }}</span>
                </div>
                <div ref="uploadTarget" :class="store.files.length || store.busy ? 'p-[12px]' : ''" />
            </template>
        </IdeoDropZone>
        <template #modal-footer><PortalTarget :name="actionsTarget" /></template>
    </IdeoModal>
</template>
